<template>
  <standard-dialog v-model="model" :title="$t('credit_only')" :max-width="600">
    <global-confirm-box ref="confirmBox"></global-confirm-box>
    <template #title-actions>
      <v-btn dark outlined @click="editCredit()">
        <span class="caption">{{ $t("edit_credit") }}</span>
      </v-btn>
      <primary-button color="#fff" class="mx-2" :dark="false" @click="addCredit()">
        <span class="caption">{{ $t("add_credit1") }}</span>
      </primary-button>
    </template>

    <div v-if="canSeeEditCredit">
      <p class="font-weight-bold">{{ $t("credit_history") }}:</p>
      <p class="text-center" v-if="!employee.credit.length">
        {{ $t("user_no_credit") }}
      </p>
      <v-data-table
        v-else
        :headers="headersCredit"
        :items="employee.credit"
        class="ordera-box-shadow striped-table"
        dense
        id="print"
        :search="search"
      >
        <template v-slot:item.credit_type="{ item }">
          {{ getCreditName(item.credit_type_id) }}
        </template>
        <template v-slot:item.date_to="{ item }">
          {{ localeDate(validTo(item.credit_type_id)) }}
        </template>
        <template v-slot:item.date="{ item }">
          {{ localeDate(item.date) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <edit-button class="mr-2" @click="creditEdit(item)"></edit-button>
          <remove-button @click="creditDelete(item)"></remove-button>
        </template>
      </v-data-table>
      <div v-if="editCredtiItem">
        <p class="mb-0 mt-3 mb-1 font-weight-bold">{{ $t("credit") }}:</p>
        <autocomplete
          :label="$t('credit_edit')"
          v-model="creditItemEdit.credit_type_id"
          :items="storedCredits"
          hide-details
          @change="selectCredit"
          item-value="id"
          item-text="name"
        ></autocomplete>
        <v-row>
          <v-col>
            <text-field
              class="mt-3"
              :min="0"
              v-model="creditItemEdit.duguje"
              type="number"
              :label="$t('enter_supplement')"
            />
          </v-col>
          <v-col>
            <text-field
              type="number"
              :min="0"
              v-model="creditItemEdit.potrazuje"
              :label="$t('enter_consumption')"
            ></text-field>
          </v-col>
        </v-row>
      </div>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="model = false">
          {{ $t("cancel") }}
        </v-btn>
        <primary-button v-if="canSeeButtons" @click="storeUserCreditEdit()">
          {{ $t("save") }}
        </primary-button>
        <v-spacer></v-spacer>
      </v-card-actions>
    </div>
    <div v-if="!canSeeEditCredit">
      <autocomplete
        :label="$t('choose_credit')"
        v-model="employee.izabrani_kredit"
        :items="storedCredits"
        @change="selectCredit"
        item-value="id"
        item-text="name"
      ></autocomplete>
      <p style="color: #949494; font-size: 11px">
        {{
          employee.izabrani_kredit
            ? $t("valid_from") +
              formatDate(
                storedCredits.find((id) => id.id == employee.izabrani_kredit).date_from
              ) +
              " - " +
              $t("valid_to") +
              formatDate(
                storedCredits.find((id) => id.id == employee.izabrani_kredit).date_to
              ) +
              " | " +
              $t("percent_colon") +
              storedCredits.find((id) => id.id == employee.izabrani_kredit).percent +
              " | " +
              $t("discount") +
              storedCredits.find((id) => id.id == employee.izabrani_kredit).rabat
            : ""
        }}
      </p>
      <text-field
        type="number"
        v-model="employee.kredit"
        required
        :label="$t('enter_credit')"
      ></text-field>
    </div>
    <template #actions>
      <v-btn text @click="model = false">
        {{ $t("cancel") }}
      </v-btn>
      <primary-button @click="storeUserCredit()">{{ $t("add") }}</primary-button>
    </template>
  </standard-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import creditService from "@/services/credit";
import StandardDialog from "@/components/dialogs/StandardDialog.vue";

export default {
  name: "CompanyEmployeeCreditDialog",
  mixins: [hasModel, hasSnackbar],
  props: {
    employee: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    StandardDialog,
  },
  data() {
    return {
      canSeeEditCredit: false,
      canSeeAddCredit: true,
      creditItemEdit: null,
      storedCredits: [],
    };
  },
  methods: {
    async getStoredCredits() {
      try {
        const { data } = await creditService.getActiveCredits(this.companyId);
        data.forEach((item) => {
          item.credit_type_id = item.id;
        });
        this.storedCredits = data;
      } catch (error) {
        console.error("Error loading credits:", error);
        this.showErrorSnackbar(this.$t("error_loading_credits"));
      }
    },
    validTo(credit) {
      let date = "";
      this.storedCredits.filter((item) => {
        if (item.id == credit) {
          date = item.date_to;
        }
      });
      return date;
    },
    creditDelete(item) {
      this.$refs.confirmBox
        .open(this.$t("delete_credit"), this.$t("delete_credit_confirmation"), {
          color: "error",
        })
        .then(async () => {
          try {
            await creditService.deleteUserCredit(item.id);
            const creditIndex = this.employee.credit.findIndex(
              (credit) => credit.id === item.id
            );
            if (creditIndex !== -1) {
              this.employee.credit.splice(creditIndex, 1);
            }
            this.showSuccessSnackbar(this.$t("credit_deleted"));
          } catch (error) {
            console.error("Error deleting credit:", error);
            this.showErrorSnackbar(this.$t("error_deleting_credit"));
          }
        })
        .catch(() => {
          // User cancelled
        });
    },
    editCredit() {
      this.canSeeEditCredit = true;
      this.canSeeAddCredit = false;
    },
    addCredit() {
      this.canSeeAddCredit = true;
      this.canSeeEditCredit = false;
      this.editCredtiItem = false;
      this.canSeeButtons = false;
    },

    creditEdit(item) {
      this.canSeeButtons = true;
      this.editCredtiItem = true;
      this.creditItemEdit = item;
    },

    async storeUserCredit() {
      try {
        const payload = {
          date: this.localeDate(new Date(), "YYYY-MM-DD HH:mm:ss"),
          credit_type_id: this.employee.credit_type_id,
          duguje: this.employee.kredit,
          user_id: this.employee.id,
        };
        const { data } = await creditService.addUserCredit(payload);
        if (data) {
          this.employee.credit.push(data);
          this.employee.izabrani_kredit = "";
          this.model = false;
          this.showSuccessSnackbar(this.$t("credit_saved"));
        }
      } catch (error) {
        console.error("Error saving credit:", error);
        this.showErrorSnackbar(this.$t("error_saving_credit"));
      }
    },
    getCreditName(credit) {
      let name = "";
      this.storedCredits.filter((item) => {
        if (item.id == credit) {
          name = item.name;
        }
      });
      return name;
    },
    selectCredit() {
      this.storedCredits.filter((item) => {
        if (item.id == this.credit) {
          this.employee = item;
          this.employee["naziv"] = item.name;
          this.employee["id"] = item.id;
        }
      });
    },
    async storeUserCreditEdit() {
      try {
        const { data } = await creditService.editUserCredit(this.creditItemEdit);
        if (data) {
          this.openModalCredit = false;
          const creditIndex = this.employee.credit.findIndex(
            (item) => item.id === data.id
          );
          if (creditIndex !== -1) {
            this.employee.credit[creditIndex] = data;
          }
          this.creditItemEdit = "";
          this.editCredtiItem = false;
          this.canSeeButtons = false;
          this.showSuccessSnackbar(this.$t("credit_updated"));
        }
      } catch (error) {
        console.error("Error updating credit:", error);
        this.showErrorSnackbar(this.$t("error_updating_credit"));
      }
    },
  },
  computed: {
    companyId() {
      return this.$route.params.id || this.$store.state.company?.id;
    },
    headersCredit() {
      return [
        {
          text: this.$t("credit_type"),
          align: "start",
          value: "credit_type",
        },
        {
          text: this.$t("supplement"),
          align: "start",
          value: "duguje",
        },
        {
          text: this.$t("consumption"),
          align: "start",
          value: "potrazuje",
        },
        {
          text: this.$t("date_to"),
          align: "start",
          value: "date_to",
        },
        {
          text: this.$t("date"),
          align: "start",
          value: "date",
        },
        {
          text: this.$t("actions"),
          align: "start",
          value: "actions",
        },
      ];
    },
  },
  watch: {
    model(newVal) {
      if (newVal) {
        this.getStoredCredits();
      }
    },
    employee: {
      immediate: true,
      handler() {
        if (this.employee.id) {
          if (this.employee.credit) {
            this.storedCredits.filter((item1) => {
              if (item1.id == item.credit.credit_type_id) {
                this.credit = item.credit.credit_type_id;
              }
            });
          } else {
            this.employee.credit = "";
          }
        }
      },
    },
  },
};
</script>
